import {CoreObject, ObjectType} from 'app-core/framework/CoreObjectConstants';
import {asCoreObject} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {getIdPrefixForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';

/**
 * This represents the specification of a core object that has not yet been instantiated.
 *
 * The primary use case here is determining platform and UI behavior in cases where we don't have a specific
 * instance(s) of a core object, yet. For example, we may want to include or exclude a column in a structured table
 * depending on what type of schema is selected by the user. From the schema, we can determine the core object type,
 * as well as properties that will be present on the object instances contained in the rows of the table. This allows
 * the creation of an EphemeralObjectSpec, which can interface with the existing platform configuration framework
 * through the use of {@link toCoreObject}.

 * Right now, this lives within the structured tables code, but it could be moved to the app-core framework if it
 * becomes useful in other contexts.
 *
 * For example, a well plate well container can be represented by the following EphemeralObjectSpec:
 * @example {objectType: DomainGraphType.CONTAINER, attributes: {isFixedPlate: true}}
 *
 */
export interface EphemeralObjectSpec {
  /** The DomainGraphType */
  objectType: ObjectType;
  /** The properties that will exist on the core object instance */
  attributes?: {[key: string]: unknown};
}

/**
 * Converts an EphemeralObjectSpec to a temporary CoreObject instance. This instance contains a dummy ID,
 * which should not be persisted. Instead, it is only to be used as a placeholder, where a concrete instance is
 * not yet available.
 */
export function toCoreObject(spec: EphemeralObjectSpec): CoreObject {
  return asCoreObject({
    id: `${getIdPrefixForObjectType(spec.objectType)}_temp`,
    ...spec.attributes,
  });
}
